/**
 * Copyright (C) 2022 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Step,
  StepLabel,
  Stepper,
  StepIconProps,
  StepConnector,
  stepConnectorClasses,
  Button,
  List,
  ListItem,
  ListItemIcon,
} from '@mui/material';
import { isNil } from 'lodash';
import { shallowEqual, useSelector } from 'react-redux';
import {
  selectFlightLegVictoryChartData,
  selectFlightLegServiceAlerts,
  selectSelectedOverlaidFlightLegId,
} from '../../../store';
import appTheme from '../../common/theme/appTheme.json';
import { getSegmentColorDefinitions } from '../../../utils/MptFlightPathSegmentingUtils';
import { Circle, Add, Remove, CircleRounded } from '@mui/icons-material';
import { useSelectorWithMapId } from '../../common/map/BaseMapContainer';
import { FlightLegChartData } from '../../../store/missionPlans/reducer';

interface PerformanceOutlookFlightEventsListProps {
  isPOExportPage: boolean;
  flightLegId?: number;

  // the number to insert a buffer div to allow for printing
  breakDataNum?: number;
}

const LANDING_ICON_CLASS = 'landingIcon';
const TAKEOFF_ICON_CLASS = 'takeoffIcon';
const ALERT_MARKER_SVG = 'alertMarkerSvg';
const ALERT_MARKER_CLASS = 'alertMarkerIcon';
const HANDOFF_MARKER_CLASS = 'handoffMarkerIcon';
const HANDOFF_MARKER_ARROWS_CLASS = 'handoffMarkerArrowsIcon';
const HANDOFF_MARKER_SVG = 'handoffMarkerSvg';

export const AlertMarkerSvg = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" className={ALERT_MARKER_SVG}>
    <path d="M0 0h24v24H0z" fill="none" />
    <rect fill={appTheme.secondaryBackgroundColor} x="10" y="5" height="13" width="4"></rect>
    <path
      className={ALERT_MARKER_CLASS}
      d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"
    />
  </svg>
);

export const HandoffMarkerSvg = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" className={HANDOFF_MARKER_SVG}>
    <path fill="none" d="M0 0h24v24H0V0z" />
    <circle cx="12" cy="12" r="9" className={HANDOFF_MARKER_ARROWS_CLASS} />
    <path
      className={HANDOFF_MARKER_CLASS}
      d="M22 12c0-5.52-4.48-10-10-10S2 6.48 2 12s4.48 10 10 10 10-4.48 10-10zm-7-5.5l3.5 3.5-3.5 3.5V11h-4V9h4V6.5zm-6 11L5.5 14 9 10.5V13h4v2H9v2.5z"
    />
  </svg>
);

export const TakeoffMarkerSvg = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    xmlnsXlink="http://www.w3.org/1999/xlink"
    width="30"
    height="30"
    viewBox="0 0 32 32"
    style={{ paddingRight: '20px', paddingLeft: '20px' }}
  >
    <defs>
      <path id="a" d="M0 0h24v24H0V0z" />
    </defs>
    <clipPath id="b">
      <use xlinkHref="#a" overflow="visible" />
    </clipPath>
    <g transform="translate(4, 4)">
      <path
        className={TAKEOFF_ICON_CLASS}
        d="M2.5 19h19v2h-19zm19.57-9.36c-.21-.8-1.04-1.28-1.84-1.06L14.92 10l-6.9-6.43-1.93.51 4.14 7.17-4.97 1.33-1.97-1.54-1.45.39 1.82 3.16.77 1.33 1.6-.43 5.31-1.42 4.35-1.16L21 11.49c.81-.23 1.28-1.05 1.07-1.85z"
        clipPath="url(#b)"
      />
      <path clipPath="url(#b)" fill="none" d="M0 0h24v24H0V0z" />
    </g>
  </svg>
);

export const LandingMarkerSvg = (fill?: string) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    xmlnsXlink="http://www.w3.org/1999/xlink"
    width="30"
    height="30"
    viewBox="0 0 32 32"
    style={{ paddingRight: '21px', paddingLeft: '21px' }}
  >
    <defs>
      <path id="a" d="M0 0h24v24H0V0z" />
    </defs>
    <clipPath id="b">
      <use xlinkHref="#a" overflow="visible" />
    </clipPath>
    <g transform="translate(4, 4)">
      <path
        className={LANDING_ICON_CLASS}
        d="M2.5 19h19v2h-19zm7.18-5.73l4.35 1.16 5.31 1.42c.8.21 1.62-.26 1.84-1.06.21-.8-.26-1.62-1.06-1.84l-5.31-1.42-2.76-9.02L10.12 2v8.28L5.15 8.95l-.93-2.32-1.45-.39v5.17l1.6.43 5.31 1.43z"
        clipPath="url(#b)"
      />
      <path clipPath="url(#b)" fill="none" d="M0 0h24v24H0V0z" />
    </g>
  </svg>
);

// Returns customized white SVGs of service alert markers (currently only used in Performance Outlook Flight Events List)
export const eventsListServiceAlertSVG = (alertType?: string) => {
  let markerImage = undefined;
  switch (alertType) {
    case 'HNA_ZONE':
    case 'HNA':
    case 'KA_HNA':
    case 'HCX_HNA':
    case 'NO_COVERAGE':
      markerImage = AlertMarkerSvg();
      break;
    case 'HAND_OFF':
      markerImage = HandoffMarkerSvg();
      break;
    case 'TAKEOFF':
      markerImage = TakeoffMarkerSvg();
      break;
    case 'LANDING':
      markerImage = LandingMarkerSvg();
      break;
    default:
      break;
  }
  return !isNil(markerImage) ? markerImage : undefined;
};

const iconWrapperSx = {
  color: appTheme.primaryTextColor,
  height: '38px',
  [`.${ALERT_MARKER_CLASS},`]: {
    fill: appTheme.mptAlertWhite,
  },
  [`.${LANDING_ICON_CLASS}, .${TAKEOFF_ICON_CLASS}, .${HANDOFF_MARKER_CLASS}`]: {
    fill: appTheme.primaryTextColor,
  },
  [`.${HANDOFF_MARKER_ARROWS_CLASS}`]: {
    fill: appTheme.secondaryBackgroundColor,
  },
  [`.${HANDOFF_MARKER_SVG}, .${ALERT_MARKER_SVG}`]: {
    paddingRight: '24px',
    paddingLeft: '24px',
  },
  '@media print': {
    color: '#000000',
    [`.${LANDING_ICON_CLASS}, .${TAKEOFF_ICON_CLASS}`]: {
      fill: '#000000',
    },
    [`.${HANDOFF_MARKER_SVG}, .${ALERT_MARKER_SVG}`]: {
      width: '34px',
      height: '34px',
      paddingRight: '19px',
      paddingLeft: '19px',
    },
    [`.${ALERT_MARKER_CLASS},`]: {
      fill: appTheme.primaryTextColor,
    },
  },
};

const STEPPER_FONT_SIZE = 18;
const ADD_REMOVE_SIZE = 16;
const DEFAULT_SPACING = 24;

const PerformanceOutlookFlightEventsList = (props: PerformanceOutlookFlightEventsListProps) => {
  const { isPOExportPage, flightLegId, breakDataNum } = props;
  const graphData = useSelector(selectFlightLegVictoryChartData, shallowEqual);
  const serviceAlerts = useSelector(selectFlightLegServiceAlerts, shallowEqual);

  /* WARNING: will need to change this if PERFORMANCE_OUTLOOK_MAP_ID (used by other PO components)
   changes to something other than the default map id (GOOGLE_MAP_INSTANCE_KEY) */
  let selectedOverlaidFlightLegId = useSelectorWithMapId(selectSelectedOverlaidFlightLegId);
  selectedOverlaidFlightLegId = !isNil(flightLegId) ? flightLegId : selectedOverlaidFlightLegId;

  const [hasUserSetTime, setHasUserSetTime] = useState<boolean>(false);
  const [eventsListShown, setEventsListShown] = useState<boolean>(false);

  const possibleColors = useMemo(() => getSegmentColorDefinitions(), []);

  useEffect(() => {
    // If takeoff alert has a user set takeoff time, then all the alerts have a zulu time specified
    setHasUserSetTime(
      !isNil(graphData) &&
        !isNil(selectedOverlaidFlightLegId) &&
        !isNil(graphData[selectedOverlaidFlightLegId]) &&
        (graphData[selectedOverlaidFlightLegId] as FlightLegChartData[])[0]?.userSetTimestamp
        ? true
        : false,
    );
  }, [graphData, selectedOverlaidFlightLegId]);

  if (
    isNil(selectedOverlaidFlightLegId) ||
    isNil(serviceAlerts) ||
    isNil(serviceAlerts[selectedOverlaidFlightLegId]) ||
    isNil(graphData) ||
    isNil(graphData[selectedOverlaidFlightLegId])
  ) {
    return null;
  }

  let serviceAlertGroupedData = [[...(graphData[selectedOverlaidFlightLegId] as FlightLegChartData[])]];

  if (breakDataNum) {
    serviceAlertGroupedData = serviceAlertGroupedData[0].reduce(
      (accum: FlightLegChartData[][], current, idx) => {
        const arrayIdx = Math.floor(idx / breakDataNum);
        const arrayToPush = accum[arrayIdx] ? accum[arrayIdx] : [];
        arrayToPush.push(current);
        accum[arrayIdx] = arrayToPush;
        return accum;
      },
      [[]],
    );
  }

  return (
    <React.Fragment>
      {
        <Box sx={{ paddingLeft: `${DEFAULT_SPACING}px` }}>
          {!isPOExportPage ? (
            <Button
              variant="text"
              sx={{ fontSize: '16px', paddingLeft: '0px', paddingRight: '5px', marginBottom: `${DEFAULT_SPACING}px` }}
              onClick={() => {
                setEventsListShown(!eventsListShown);
              }}
            >
              <Box display="flex" alignItems="center">
                {eventsListShown ? (
                  <Remove sx={{ width: `${ADD_REMOVE_SIZE}px`, height: `${ADD_REMOVE_SIZE}px` }} />
                ) : (
                  <Add sx={{ width: `${ADD_REMOVE_SIZE}px`, height: `${ADD_REMOVE_SIZE}px` }} />
                )}
              </Box>
              <Box component="u" sx={{ paddingLeft: '8px' }}>{`${
                eventsListShown ? 'Hide' : 'Show'
              } Flight Events List`}</Box>
            </Button>
          ) : (
            <></>
          )}
          {isPOExportPage
            ? serviceAlertGroupedData.map((groupedData, idx) => {
                return (
                  <>
                    <List dense disablePadding>
                      {groupedData.map(data => {
                        const connectionColor = data?.marginalService
                          ? possibleColors.mptMarginalServiceColor
                          : data.color;
                        return (
                          <ListItem dense sx={iconWrapperSx}>
                            {hasUserSetTime && <Box sx={{ fontSize: '13px' }}>{data.userSetTimestamp}</Box>}
                            <ListItemIcon sx={{ minWidth: '13px', paddingX: '4px' }}>
                              <CircleRounded sx={{ color: connectionColor, fontSize: '13px' }} />
                            </ListItemIcon>
                            <Box sx={{ fontSize: '13px' }}>{data.timestamp}</Box>
                            <ListItemIcon sx={{ fontSize: '13px' }}>
                              {eventsListServiceAlertSVG(data.type)}
                            </ListItemIcon>
                            <Box sx={{ fontSize: '13px' }}>{data.description}</Box>
                          </ListItem>
                        );
                      })}
                    </List>
                  </>
                );
              })
            : eventsListShown && (
                <Stepper
                  orientation="vertical"
                  connector={
                    isPOExportPage ? null : (
                      <StepConnector
                        sx={{
                          paddingLeft: hasUserSetTime ? '75px' : '1px',
                          marginLeft: '8px',
                          [`& .${stepConnectorClasses.line}`]: {
                            borderColor: appTheme.mptSelectIconGray,
                            height: '26px',
                            borderWidth: '1px',
                          },
                        }}
                      />
                    )
                  }
                >
                  {(graphData[selectedOverlaidFlightLegId] as FlightLegChartData[]).map(data => {
                    const connectionColor = data?.marginalService ? possibleColors.mptMarginalServiceColor : data.color;

                    const CircleIcon = (iconProps: StepIconProps) => {
                      const { sx } = iconProps;
                      return (
                        <Box display="flex" alignItems="center" sx={{ paddingLeft: '2px' }}>
                          {hasUserSetTime && (
                            <Box
                              sx={{
                                paddingRight: `${DEFAULT_SPACING}px`,
                                color: appTheme.primaryTextColor,
                                fontSize: `${STEPPER_FONT_SIZE}px`,
                                fontWeight: 'regular',
                              }}
                            >
                              {data.userSetTimestamp}
                            </Box>
                          )}
                          <Circle sx={sx} />
                        </Box>
                      );
                    };

                    return (
                      <Step key={`${data.timeSinceTakeOffMin} ${data.y}`} completed>
                        <StepLabel
                          StepIconComponent={CircleIcon}
                          StepIconProps={{ sx: { color: connectionColor, fontSize: '16px' } }}
                        >
                          <Box
                            display="flex"
                            alignItems="center"
                            sx={{
                              ...iconWrapperSx,
                              color: appTheme.primaryTextColor,
                              fontSize: `${STEPPER_FONT_SIZE}px`,
                              fontWeight: 'regular',
                              [`@media print`]: {
                                color: 'black',
                              },
                            }}
                          >
                            {/* Timestamp */}
                            <Box paddingLeft="16px">{data.timestamp}</Box>

                            {/* Service Alert SVG */}
                            {eventsListServiceAlertSVG(data.type)}

                            {/* Description */}
                            <Box>{`${data.description}`}</Box>
                          </Box>
                        </StepLabel>
                      </Step>
                    );
                  })}
                </Stepper>
              )}
        </Box>
      }
    </React.Fragment>
  );
};

export default PerformanceOutlookFlightEventsList;
